import { QUERY_KEY_MANAGEMENT_SEARCH_UNITS } from '@apps-common/apis/management/unit'
import { useManagementUpdateSingleCancelledNonProcessed } from '@apps-common/apis/management/unit-cancelled'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_ACTIVATION_ALLOWED_PRODUCTS } from '@apps-common/config/product-codes'
import { UNIT_STATUS_CANCELLED, UNIT_STATUS_INACTIVE, UNIT_STATUS_PENDING_CANCELLATION } from '@apps-common/config/unit-filter'
import { IAppNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { IManagementUnitsFilterState } from '@apps-common/stores'
import { IManagementUnitSearchModel } from '@apps-common/types/management/unit'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import {
  ROUTE_ORDER_DETAILS,
  ROUTE_UNIT_ACTIVATION_DETAILS,
  ROUTE_UNIT_DETAILS,
  ROUTE_UNIT_MANAGE,
  ROUTE_UNIT_PROGRAM_TASK_QUEUE
} from '@apps-radionet-portal-root/src/config/private-routes'

import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { navigateActivationWithUnitDetails } from '../../../pages/activations/navigateActivationWithUnitDetails'

interface IActionProps {
  props: IManagementUnitSearchModel
  navigateForward: IAppNavigation['navigateForward']
}

const Actions: React.FC<IActionProps> = ({ props, navigateForward }) => {
  const { isPending: makProcessedPending, mutate: markProcessed } = useManagementUpdateSingleCancelledNonProcessed(true, () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS() })
  })

  return (
    <div className='flex items-center justify-end gap-[8px]'>
      {props.activeTaskGroupID && props.activeTaskQueueID && (
        <ButtonRoundSm
          type='button'
          className='action-warning'
          callback={() =>
            navigateForward({
              routeUrl: ROUTE_UNIT_PROGRAM_TASK_QUEUE.buildPath(props.activeTaskGroupID, props.activeTaskQueueID),
              route: ROUTE_UNIT_PROGRAM_TASK_QUEUE
            })
          }
          tooltip={{ id: `device-active-program-task-${props.unitId}`, message: 'Active task' }}
        >
          <commonIcons.unitProgramTaskSchedule className='text-[1.1rem]' />
        </ButtonRoundSm>
      )}
      {props.orderId && (
        <ButtonRoundSm
          type='button'
          className='action-success'
          callback={() => navigateForward({ routeUrl: ROUTE_ORDER_DETAILS.buildPath(props.orderId), route: ROUTE_ORDER_DETAILS })}
          tooltip={{ id: `device-order-${props.unitId}`, message: 'Related order' }}
        >
          <commonIcons.sbOrders />
        </ButtonRoundSm>
      )}
      {props.status === UNIT_STATUS_INACTIVE.value && UNIT_ACTIVATION_ALLOWED_PRODUCTS.includes(props.productId) && (
        <ButtonRoundSm
          type='button'
          className={props.unitActivationRequestID ? 'action-warning' : 'action-success'}
          callback={() => {
            if (props.unitActivationRequestID) {
              navigateForward({
                routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(props.unitActivationRequestID),
                route: ROUTE_UNIT_ACTIVATION_DETAILS
              })
            } else {
              navigateActivationWithUnitDetails(navigateForward, props.unitId, props.serialNo, props.imei)
            }
          }}
          tooltip={{
            id: props.unitActivationRequestID
              ? `view-device-activation-request-${props.unitId}`
              : `create-device-activation-request-${props.unitId}`,
            message: props.unitActivationRequestID ? 'View activation' : 'Create activation'
          }}
        >
          <commonIcons.srActivations />
        </ButtonRoundSm>
      )}

      {props.status === UNIT_STATUS_CANCELLED.value && !props.cancellationProcessed && (
        <ModalButton
          component={ConfirmModal}
          disabled={makProcessedPending}
          classes=''
          componentProps={{
            text: 'Mark processed',
            description: 'Confirm you would like to mark this sim as processed?',
            action: ConfirmModalAction.Error
          }}
          confirmCallback={() => markProcessed({ unitId: props.unitId })}
        >
          <ButtonRoundSm
            type='div'
            className='action-warning'
            tooltip={{ id: `mark-cancelled-sim-processed-${props.unitId}`, message: 'Mark sim processed' }}
          >
            <commonIcons.processSimCancellation className='text-[1.2em]' />
          </ButtonRoundSm>
        </ModalButton>
      )}

      <ButtonRoundSm
        type='button'
        className='action-info'
        callback={() => {
          navigateForward({ routeUrl: ROUTE_UNIT_DETAILS.buildPath(props.status, props.unitId), route: ROUTE_UNIT_DETAILS })
        }}
        tooltip={{ id: `device-details-${props.unitId}`, message: 'Details' }}
      >
        <commonIcons.details />
      </ButtonRoundSm>
      <ButtonRoundSm
        type='button'
        className='bg-brand text-on-brand'
        callback={() => {
          navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(props.status, props.unitId), route: ROUTE_UNIT_MANAGE })
        }}
        tooltip={{ id: `manage-device-${props.unitId}`, message: 'Manage' }}
      >
        <commonIcons.arrowAngled />
      </ButtonRoundSm>
    </div>
  )
}

export const activeKeys = ({ navigateForward }: IAppNavigation) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },

  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Sim',
    key: 'simNo',
    changeable: true,
    thClasses: 'hidden xl:table-cell',
    tdClasses: 'hidden xl:table-cell'
  },

  {
    title: 'IMEI',
    key: 'imei',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  // options - start
  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Technician',
    key: 'tech',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IP',
    key: 'ipAddress',
    optional: true,
    thClasses: 'lg:table-cell',
    tdClasses: 'lg:table-cell'
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Server',
    key: 'serverKey',
    optional: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => (
      <>
        {(!props?.serverKey || props?.serverKey?.length === 0) && <div>{'<None assigned>'}</div>}
        {props?.serverKey && props?.serverKey?.length > 0 && <div>{props?.serverKey}</div>}
      </>
    )
  },
  {
    title: 'Requires invoicing',
    key: 'hardwareToBeInvoiced',
    optional: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <span>{props.hardwareToBeInvoiced ? 'Yes' : 'No'}</span>
  },
  // optional -end
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right ',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigateForward} />
  }
]

const inactiveKeys = ({ navigateForward }: IAppNavigation) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'IMEI',
    key: 'imei',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'Loaded on',
    key: 'dateLoaded',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.dateLoaded, 'DD/MM/YYYY')}</span>
  },

  // optional - start
  {
    title: 'Account',
    key: 'accountNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Technician',
    key: 'tech',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Sim',
    key: 'simNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },

  // optional - end

  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right ',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigateForward} />
  }
]

const pendingCancellationKeys = ({ navigateForward }: IAppNavigation) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },

  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Cancels on',
    key: 'pendingCancellationDate',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.pendingCancellationDate || '', 'DD/MM/YYYY')}</span>
  },

  // optional - start
  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Technician',
    key: 'tech',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Sim',
    key: 'simNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },

  // optional - end
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right ',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigateForward} />
  }
]

const cancelledKeys = ({ navigateForward }: IAppNavigation) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },

  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Status',
    key: 'cancellationProcessed',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => (
      <span>
        {props.cancellationProcessed ? (
          <span className='text-[0.8rem] bg-error text-on-error px-3 py-1 rounded-full'>Processed</span>
        ) : (
          <span className='text-[0.8rem] bg-warning text-on-warning px-3 py-1 rounded-full'>Pending</span>
        )}
      </span>
    )
  },
  {
    title: 'Cancel date',
    key: 'cancellationDate',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.cancellationDate || '', 'DD/MM/YYYY')}</span>
  },

  // optional - start

  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Technician',
    key: 'tech',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Sim',
    key: 'simNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },

  // optional - end
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right ',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigateForward} />
  }
]

export const buildTableKeys = (navigation: IAppNavigation, filter: IManagementUnitsFilterState) => {
  if (filter.status === UNIT_STATUS_INACTIVE.value) {
    return inactiveKeys(navigation)
  }

  if (filter.status === UNIT_STATUS_PENDING_CANCELLATION.value) {
    return pendingCancellationKeys(navigation)
  }

  if (filter.status === UNIT_STATUS_CANCELLED.value) {
    return cancelledKeys(navigation)
  }

  return activeKeys(navigation)
}
